import React from 'react';
import {
    Box,
    Stack,
    TextField,
    Dropdown,
    Button,
    Columns,
    Column,
    Text,
    Divider,
    Actions,
    Drawer,
    Card,
} from 'braid-design-system';
import { useToast } from '../../hooks/useToast';
import DateField from '../DateField';
import { useTranslation } from 'react-i18next';
import {
    CiCustomerNicRecord,
    NicExtractedDetails,
    uploadNicPhotos,
    extractNic,
    updateNic,
} from '../../services/nicService';

interface NicVerificationDrawerProps {
    open: boolean;
    record: CiCustomerNicRecord;
    onClose: () => void;
    onVerified: (record: CiCustomerNicRecord) => void;
}

const emptyText = { en: '', si: '', ta: '' };

const NicVerificationDrawer: React.FC<NicVerificationDrawerProps> = ({
    open,
    record,
    onClose,
    onVerified,
}) => {
    const toast = useToast();
    const { t } = useTranslation();

    // Upload state
    const [frontFile, setFrontFile] = React.useState<File | null>(null);
    const [backFile, setBackFile] = React.useState<File | null>(null);
    const [uploading, setUploading] = React.useState(false);
    const [extracting, setExtracting] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const [photosSaved, setPhotosSaved] = React.useState<boolean>(!!(record.photo?.front && record.photo?.back));
    // Details section is only shown after extraction.
    const [detailsShown, setDetailsShown] = React.useState(false);

    // Editable extraction state
    const [name, setName] = React.useState({ ...emptyText, ...(record.name || {}) });
    const [address, setAddress] = React.useState({ ...emptyText, ...(record.address || {}) });
    const [placeOfBirth, setPlaceOfBirth] = React.useState<{ en: string; si: string; ta: string }>({
        ...emptyText,
        ...(record.place_of_birth || {}),
    });
    const [sex, setSex] = React.useState(record.sex || '');
    const [dateOfIssue, setDateOfIssue] = React.useState(record.date_of_issue ? record.date_of_issue.slice(0, 10) : '');
    const [dateOfBirth, setDateOfBirth] = React.useState(record.date_of_birth ? record.date_of_birth.slice(0, 10) : '');

    const applyExtracted = (details: NicExtractedDetails) => {
        if (details.name) setName({ ...emptyText, ...details.name });
        if (details.address) setAddress({ ...emptyText, ...details.address });
        if (details.place_of_birth) setPlaceOfBirth({ ...emptyText, ...details.place_of_birth });
        if (details.sex) setSex(details.sex);
        if (details.date_of_issue) setDateOfIssue(details.date_of_issue.slice(0, 10));
        if (details.date_of_birth) setDateOfBirth(details.date_of_birth.slice(0, 10));
    };

    const handleUpload = async () => {
        if (!frontFile || !backFile) {
            toast.showWarning(
                t('page.approve_mobile_app_customer.form.nic.warning.title', { defaultValue: 'NIC Verification' }),
                t('page.approve_mobile_app_customer.form.nic.warning.photos_required', { defaultValue: 'Please select both NIC front and back images.' })
            );
            return;
        }
        setUploading(true);
        try {
            await uploadNicPhotos(record.id, frontFile, backFile);
            setPhotosSaved(true);
            toast.showSuccess(
                t('page.approve_mobile_app_customer.form.nic.upload.success.title', { defaultValue: 'Success' }),
                t('page.approve_mobile_app_customer.form.nic.upload.success.message', { defaultValue: 'NIC photos uploaded successfully.' })
            );
            // Automatically extract details right after a successful upload.
            await handleExtract();
        } catch (error: any) {
            toast.showError(
                t('page.approve_mobile_app_customer.form.nic.upload.error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile_app_customer.form.nic.upload.error.message', { defaultValue: 'Failed to upload NIC photos.' })
            );
        } finally {
            setUploading(false);
        }
    };

    const handleExtract = async () => {
        setExtracting(true);
        try {
            const details = await extractNic(record.id);
            // Always show the details section so the user can review/fill, whether or not
            // the extraction returned data.
            setDetailsShown(true);
            applyExtracted(details);
            toast.showSuccess(
                t('page.approve_mobile_app_customer.form.nic.extract.success.title', { defaultValue: 'Success' }),
                t('page.approve_mobile_app_customer.form.nic.extract.success.message', { defaultValue: 'Details extracted. Please review and edit before submitting.' })
            );
        } catch (error: any) {
            // Still reveal the details section so the user can enter the fields manually.
            setDetailsShown(true);
            toast.showError(
                t('page.approve_mobile_app_customer.form.nic.extract.error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile_app_customer.form.nic.extract.error.message', { defaultValue: 'Failed to extract NIC details. Please enter them manually.' })
            );
        } finally {
            setExtracting(false);
        }
    };

    const handleSubmit = async () => {
        if (!name.en || !address.en) {
            toast.showWarning(
                t('page.approve_mobile_app_customer.form.nic.warning.title', { defaultValue: 'NIC Verification' }),
                t('page.approve_mobile_app_customer.form.nic.warning.en_required', { defaultValue: 'Full Name (English) and Address (English) are required.' })
            );
            return;
        }
        if (!sex) {
            toast.showWarning(
                t('page.approve_mobile_app_customer.form.nic.warning.title', { defaultValue: 'NIC Verification' }),
                t('page.approve_mobile_app_customer.form.nic.warning.sex_required', { defaultValue: 'Sex is required.' })
            );
            return;
        }
        if (!dateOfBirth) {
            toast.showWarning(
                t('page.approve_mobile_app_customer.form.nic.warning.title', { defaultValue: 'NIC Verification' }),
                t('page.approve_mobile_app_customer.form.nic.warning.dob_required', { defaultValue: 'Date of Birth is required.' })
            );
            return;
        }

        const payload: NicExtractedDetails = {
            name,
            address,
            place_of_birth: {
                en: placeOfBirth.en,
                ...(placeOfBirth.si ? { si: placeOfBirth.si } : {}),
                ...(placeOfBirth.ta ? { ta: placeOfBirth.ta } : {}),
            },
            sex,
            date_of_issue: dateOfIssue || undefined,
            date_of_birth: dateOfBirth,
        };

        setSubmitting(true);
        try {
            const updated = await updateNic(record.id, payload);
            // Auto-fill the add form and close the drawer on success (no Done click needed).
            onVerified(updated);
        } catch (error: any) {
            toast.showError(
                t('page.approve_mobile_app_customer.form.nic.submit.error.title', { defaultValue: 'Error' }),
                error?.message ?? t('page.approve_mobile_app_customer.form.nic.submit.error.message', { defaultValue: 'Failed to save NIC details.' })
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer id="nic-verification-drawer" title="NIC Verification" open={open} width="large" onClose={onClose}>
            <Stack space="large">
                <Text>
                    {t('page.approve_mobile_app_customer.form.nic.description', {
                        defaultValue: 'This NIC ({{nic}}) is not yet verified. Upload the front and back images of the NIC, then extract the details.',
                        nic: record.nic_number,
                    })}
                </Text>

                <Divider />

                {/* Step 1 - Upload NIC photos */}
                <Stack space="medium">
                    <Text weight="strong">{t('page.approve_mobile_app_customer.form.nic.upload.title', { defaultValue: 'Upload NIC Photos' })}</Text>
                    <Columns space="small" collapseBelow="tablet">
                        <Column>
                            <Card>
                                <Stack space="small">
                                    <Text weight="strong" size="small">{t('page.approve_mobile_app_customer.form.nic.upload.front', { defaultValue: 'NIC Front' })}</Text>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setFrontFile(e.target.files?.[0] || null)}
                                        disabled={photosSaved}
                                    />
                                    {frontFile && <Text size="small" tone="secondary">{frontFile.name}</Text>}
                                </Stack>
                            </Card>
                        </Column>
                        <Column>
                            <Card>
                                <Stack space="small">
                                    <Text weight="strong" size="small">{t('page.approve_mobile_app_customer.form.nic.upload.back', { defaultValue: 'NIC Back' })}</Text>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setBackFile(e.target.files?.[0] || null)}
                                        disabled={photosSaved}
                                    />
                                    {backFile && <Text size="small" tone="secondary">{backFile.name}</Text>}
                                </Stack>
                            </Card>
                        </Column>
                    </Columns>
                    {!photosSaved ? (
                        <Actions>
                            <Button onClick={handleUpload} loading={uploading} tone="brandAccent">
                                {t('page.approve_mobile_app_customer.form.nic.upload.button', { defaultValue: 'Upload' })}
                            </Button>
                        </Actions>
                    ) : !detailsShown ? (
                        <Actions>
                            <Button loading={extracting} tone="brandAccent">
                                {t('page.approve_mobile_app_customer.form.nic.extract.button', { defaultValue: 'Extracting...' })}
                            </Button>
                        </Actions>
                    ) : null}
                </Stack>

                {/* Step 2 - NIC details (only shown after extraction) */}
                {detailsShown && (
                    <>
                        <Divider />

                        <Stack space="medium">
                            <Text weight="strong">{t('page.approve_mobile_app_customer.form.nic.details.title', { defaultValue: 'NIC Details' })}</Text>

                            <Columns space="small" collapseBelow="tablet">
                                <Column>
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.full_name_ln1', { defaultValue: 'Full Name (English) *' })}
                                        value={name.en}
                                        onChange={(e) => setName({ ...name, en: e.currentTarget.value })}
                                    />
                                </Column>
                                <Column>
                                    <div className="sinhala-input">
                                        <TextField
                                            label={t('page.approve_mobile_app_customer.form.field.full_name_ln2', { defaultValue: 'Full Name (Sinhala)' })}
                                            value={name.si}
                                            onChange={(e) => setName({ ...name, si: e.currentTarget.value })}
                                        />
                                    </div>
                                </Column>
                                <Column>
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.full_name_ln3', { defaultValue: 'Full Name (Tamil)' })}
                                        value={name.ta}
                                        onChange={(e) => setName({ ...name, ta: e.currentTarget.value })}
                                    />
                                </Column>
                            </Columns>

                            <Columns space="small" collapseBelow="tablet">
                                <Column>
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.address_ln1', { defaultValue: 'Address (English) *' })}
                                        value={address.en}
                                        onChange={(e) => setAddress({ ...address, en: e.currentTarget.value })}
                                    />
                                </Column>
                                <Column>
                                    <div className="sinhala-input">
                                        <TextField
                                            label={t('page.approve_mobile_app_customer.form.field.address_ln2', { defaultValue: 'Address (Sinhala)' })}
                                            value={address.si}
                                            onChange={(e) => setAddress({ ...address, si: e.currentTarget.value })}
                                        />
                                    </div>
                                </Column>
                                <Column>
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.field.address_ln3', { defaultValue: 'Address (Tamil)' })}
                                        value={address.ta}
                                        onChange={(e) => setAddress({ ...address, ta: e.currentTarget.value })}
                                    />
                                </Column>
                            </Columns>

                            <Columns space="small" collapseBelow="tablet">
                                <Column>
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.nic.field.place_of_birth_en', { defaultValue: 'Place of Birth (English)' })}
                                        value={placeOfBirth.en}
                                        onChange={(e) => setPlaceOfBirth({ ...placeOfBirth, en: e.currentTarget.value })}
                                    />
                                </Column>
                                <Column>
                                    <div className="sinhala-input">
                                        <TextField
                                            label={t('page.approve_mobile_app_customer.form.nic.field.place_of_birth_si', { defaultValue: 'Place of Birth (Sinhala)' })}
                                            value={placeOfBirth.si}
                                            onChange={(e) => setPlaceOfBirth({ ...placeOfBirth, si: e.currentTarget.value })}
                                        />
                                    </div>
                                </Column>
                                <Column>
                                    <TextField
                                        label={t('page.approve_mobile_app_customer.form.nic.field.place_of_birth_ta', { defaultValue: 'Place of Birth (Tamil)' })}
                                        value={placeOfBirth.ta}
                                        onChange={(e) => setPlaceOfBirth({ ...placeOfBirth, ta: e.currentTarget.value })}
                                    />
                                </Column>
                            </Columns>

                            <Columns space="small" collapseBelow="tablet">
                                <Column>
                                    <Dropdown
                                        label={t('page.approve_mobile_app_customer.form.nic.field.sex', { defaultValue: 'Sex *' })}
                                        value={sex}
                                        onChange={(e) => setSex(e.currentTarget.value)}
                                    >
                                        <option value="">{t('page.approve_mobile_app_customer.form.nic.placeholder.select_sex', { defaultValue: 'Select Sex' })}</option>
                                        <option value="MALE">{t('page.approve_mobile_app_customer.form.nic.option.male', { defaultValue: 'Male' })}</option>
                                        <option value="FEMALE">{t('page.approve_mobile_app_customer.form.nic.option.female', { defaultValue: 'Female' })}</option>
                                        <option value="OTHER">{t('page.approve_mobile_app_customer.form.nic.option.other', { defaultValue: 'Other' })}</option>
                                    </Dropdown>
                                </Column>
                                <Column>
                                    <DateField
                                        label={t('page.approve_mobile_app_customer.form.nic.field.date_of_issue', { defaultValue: 'Date of Issue' })}
                                        onlyForToday={false}
                                        onChange={(value) => setDateOfIssue(value || '')}
                                        value={dateOfIssue}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.date', { defaultValue: 'YYYY-MM-DD' })}
                                    />
                                </Column>
                                <Column>
                                    <DateField
                                        label={t('page.approve_mobile_app_customer.form.field.dob', { defaultValue: 'Date of Birth *' })}
                                        onlyForToday={true}
                                        onChange={(value) => setDateOfBirth(value || '')}
                                        value={dateOfBirth}
                                        placeholder={t('page.approve_mobile_app_customer.form.placeholder.date', { defaultValue: 'YYYY-MM-DD' })}
                                    />
                                </Column>
                            </Columns>

                            <Box display="flex" justifyContent="flexEnd">
                                <Actions>
                                    <Button variant="ghost" onClick={onClose}>
                                        {t('page.approve_mobile_app_customer.form.button.cancel', { defaultValue: 'Cancel' })}
                                    </Button>
                                    <Button onClick={handleSubmit} loading={submitting} tone="brandAccent">
                                        {t('page.approve_mobile_app_customer.form.nic.submit.button', { defaultValue: 'Submit' })}
                                    </Button>
                                </Actions>
                            </Box>
                        </Stack>
                    </>
                )}
            </Stack>
        </Drawer>
    );
};

export default NicVerificationDrawer;
